import { Action, StoreType } from 'reducers/types';
import {
  FETCH_ACTIVITIES,
  LOG_EXTERNAL_WORKOUT,
  UPDATE_EXTERNAL_WORKOUT,
} from '../../actions/activities';

import { Activity, ProgramFilterOption } from './types';

const initialState = {
  loading: false,
  error: false,
  search: null,
  programId: null,
  total: 0,
  all: [],
  programs: [],
  offset: 0,
  limit: 10,
  totalPrograms: 0,
  totalSessions: 0,
  totalTime: 0,
  isLoggingExternal: false,
  logExternalError: null,
  isUpdatingExternal: false,
  updateExternalError: null,
};

type ActivityPayload = {
  search?: string;
  offset?: number;
  programId?: number | null;
  error?: string;
  workout?: Activity;
  data?: {
    all: Activity[];
    programs?: ProgramFilterOption[];
    totalSessions?: number;
    totalPrograms?: number;
    totalTime?: number;
    total: number;
  };
};

export default function Index(state = initialState, action: Action<ActivityPayload>) {
  switch (action.type) {
    case FETCH_ACTIVITIES.request: {
      // Keep the existing list when paginating (load more); reset it for a new
      // search/program filter so the incoming page replaces the old results.
      const isLoadMore = !!action.payload?.offset;

      return {
        ...state,
        all: isLoadMore ? state.all : initialState.all,
        search: action.payload?.search ?? '',
        programId: action.payload?.programId ?? null,
        offset: action.payload?.offset ?? 0,
      };
    }

    case FETCH_ACTIVITIES.success: {
      const receivedItems = action?.payload?.data?.all ?? [];

      return {
        ...initialState,
        search: action.payload?.search,
        programId: action.payload?.programId ?? null,
        programs: action?.payload?.data?.programs ?? state.programs,
        total: action?.payload?.data?.total ?? 0,
        offset: action.payload?.offset ?? 0,
        totalPrograms: action?.payload?.data?.totalPrograms ?? 0,
        totalSessions: action?.payload?.data?.totalSessions ?? 0,
        totalTime: action?.payload?.data?.totalTime ?? 0,
        all: [...state.all, ...receivedItems],
      };
    }

    case FETCH_ACTIVITIES.failure: {
      return {
        ...initialState,
        ...state,
        error: true,
        all: [],
      };
    }

    case LOG_EXTERNAL_WORKOUT.request: {
      return {
        ...state,
        isLoggingExternal: true,
        logExternalError: null,
      };
    }

    case LOG_EXTERNAL_WORKOUT.success: {
      return {
        ...state,
        isLoggingExternal: false,
        logExternalError: null,
      };
    }

    case LOG_EXTERNAL_WORKOUT.failure: {
      return {
        ...state,
        isLoggingExternal: false,
        logExternalError: action.payload?.error ?? 'Something went wrong',
      };
    }

    case UPDATE_EXTERNAL_WORKOUT.request: {
      return {
        ...state,
        isUpdatingExternal: true,
        updateExternalError: null,
      };
    }

    case UPDATE_EXTERNAL_WORKOUT.success: {
      const workout = action.payload?.workout;
      const all = (state.all as Activity[]).map((item) =>
        workout && item.type === 'external' && item.id === workout.id ? workout : item,
      );

      return {
        ...state,
        isUpdatingExternal: false,
        updateExternalError: null,
        // The date may have changed; keep the feed newest-first (sort is stable,
        // so items sharing a date keep their order)
        all: all.sort(
          (a, b) => new Date(b.completedAt ?? 0).getTime() - new Date(a.completedAt ?? 0).getTime(),
        ),
      };
    }

    case UPDATE_EXTERNAL_WORKOUT.failure: {
      return {
        ...state,
        isUpdatingExternal: false,
        updateExternalError: action.payload?.error ?? 'Something went wrong',
      };
    }

    default:
      return state;
  }
}

export const getTotalSessions = (state: StoreType) => state.activities.totalSessions;

export const getTotalTime = (state: StoreType) => state.activities.totalTime;
